import React, { useContext } from 'react';
import { useIntl } from 'react-intl';
import { Grid, Hidden } from '@mui/material';
import * as css from './rightsandprivacy.module.scss';
import SiktLogo from '../login/siktLogo';
import translations from './rightsandprivacy.i18n';
import {
  BottomLinks,
  DesktopPageTop,
  SmallPageTop,
} from '../login/commoncomponents';
import classNames from 'classnames';
import { useQuery } from '../api/useQuery';
import { useSetDocumentTitle } from '../api/useSetDocumentTitle';
import { LoginApiContext } from '../api/LoginApiContext';
import { useLoader } from '../api/useLoader';
import { RoletypeDto } from '../api/generated/login-generated-api';
import PageLayout from '../pagelayout';

const RightsAndPrivacyPage = () => {
  const { formatMessage } = useIntl();
  const target = useQuery().get('for')!;
  const labels = {
    title: formatMessage({ id: translations.title.id }),
    pageTitleStudent: formatMessage({ id: translations.pageTitleStudent.id }),
    pageTitleStudiested: formatMessage({
      id: translations.pageTitleStudiested.id,
    }),
    pageTitlePraksissted: formatMessage({
      id: translations.pageTitlePraksissted.id,
    }),
    descriptionStudent: formatMessage({
      id: translations.descriptionStudent.id,
    }),
    descriptionStudiested: formatMessage({
      id: translations.descriptionStudiested.id,
    }),
    descriptionPraksissted: formatMessage({
      id: translations.descriptionPraksissted.id,
    }),
    arbeidslivsportalen: formatMessage({
      id: translations.arbeidslivsportalen.id,
    }),
  };

  const { pagetitle, description } =
    target === 'studiested'
      ? {
          pagetitle: labels.pageTitleStudiested,
          description: labels.descriptionStudiested,
        }
      : target === 'student'
      ? {
          pagetitle: labels.pageTitleStudent,
          description: labels.descriptionStudent,
        }
      : {
          pagetitle: labels.pageTitlePraksissted,
          description: labels.descriptionPraksissted,
        };
  useSetDocumentTitle(pagetitle, [target]);

  return (
    <>
      <div className={classNames(css.page, css.rightsAndPrivacyPage)}>
        <Hidden only={['md', 'lg', 'xl']}>
          <div className={css.smallPage}>
            <SmallPageTop
              css={css}
              title={labels.arbeidslivsportalen}
              description={description}
            />
            <main>
              <RightsAndPrivacy target={target} />
            </main>
          </div>
        </Hidden>
        <Hidden only={['xs', 'sm']}>
          <div className={css.desktop}>
            <DesktopPageTop
              css={css}
              title={labels.arbeidslivsportalen}
              description={description}
            />
            <main>
              <div className={css.space150} />
              <div className={css.space150} />
              <RightsAndPrivacy target={target} />
            </main>
          </div>
        </Hidden>

        <footer>
          <div className={css.space50} />
          <Grid container={true}>
            <Grid item={true} xs={1} />
            <Grid item={true} xs={10}>
              <SiktLogo />
            </Grid>
            <Grid item={true} xs={1} />
          </Grid>

          <BottomLinks css={css} />
        </footer>
      </div>
    </>
  );
};

interface RightsAndPrivacyProps {
  target: string;
}
const RightsAndPrivacy: React.FC<
  React.PropsWithChildren<RightsAndPrivacyProps>
> = ({ target }) => {
  return (
    <div className={css.policy} data-cy={'contentPolicy'}>
      {target === 'student' && <PrivacyPolicyStudent headerStartLevel={1} />}
      {target === 'studiested' && (
        <PrivacyPolicyStudiested headerStartLevel={1} />
      )}
      {target === 'praksissted' && (
        <PrivacyPolicyPraksissted headerStartLevel={1} />
      )}
    </div>
  );
};

const wrapInHeader = (headerLevel: number, text: string) => {
  switch (headerLevel) {
    case 1:
      return <h1>{text}</h1>;
    case 2:
      return <h2>{text}</h2>;
    case 3:
      return <h3>{text}</h3>;
    case 4:
      return <h4>{text}</h4>;
    case 5:
      return <h5>{text}</h5>;
    default:
      return <h6>{text}</h6>;
  }
};

const PrivacyPolicyStudent: React.FC<
  React.PropsWithChildren<{
    headerStartLevel: number;
    userRole?: string;
  }>
> = ({ userRole, headerStartLevel }) => {
  const { locale } = useIntl();

  return locale === 'nb' ? (
    <>
      {wrapInHeader(
        headerStartLevel,
        'Personvernerklæring for Arbeidslivsportalen',
      )}

      <p>
        Denne personvernerklæringen beskriver hvordan vi håndterer dine
        personopplysninger i tjenesten Arbeidslivsportalen og datasystemet FS.
        Formålet med denne personvernerklæringen er å informere deg om hvilke
        personopplysninger som behandles, hvordan de behandles, hvem som er
        ansvarlig for behandlingen, hvilke rettigheter du har og hvem du kan
        kontakte.
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, 'Innhold')}
        <ol>
          <li>Hva regnes som personopplysninger?</li>
          <li>Kort om Arbeidslivsportalen</li>
          <li>
            Formålet med, samt rettslig grunnlag for behandling av
            personopplysninger i Arbeidslivsportalen
          </li>
          <li>
            Hvilke personopplysninger blir behandlet i Arbeidslivsportalen og
            hvor lenge lagrer vi dine personopplysninger?
          </li>
          <li>I noen tilfeller utfører vi automatisk saksbehandling</li>
          <li>Vi utleverer dine personopplysninger til andre</li>
          <li>Sikkerheten rundt dine personopplysninger</li>
          <li>Dine rettigheter</li>
          <li>Kontakt</li>
        </ol>
      </div>
      {wrapInHeader(
        headerStartLevel + 1,
        '1. Hva regnes som personopplysninger?',
      )}

      <p>
        Personopplysninger er alle former for data, informasjon, opplysninger og
        vurderinger som kan knyttes til deg som enkeltperson, jf. GDPR artikkel
        4 nr. 1. Det avgjørende for om en opplysning er en personopplysning er
        om opplysningene kan knyttes til en identifisert eller identifiserbar
        fysisk person.
      </p>

      <p>
        Opplysninger som alene ikke kan knyttes til en enkeltperson, kan i
        tilfeller hvor de forekommer sammen med andre data utgjøre en
        personopplysning hvis de indirekte identifiserer en person.
      </p>

      {wrapInHeader(headerStartLevel + 1, '2. Kort om Arbeidslivsportalen')}

      <p>
        Arbeidslivsportalen er en webapplikasjon for håndtering av
        studentpraksis. I tjenesten planlegges praksisperioder og studenter kan
        - hvis det åpnes for det - søke seg til prakssisted. Arbeidslivsportalen
        er knyttet til det studieadministrative systemet Felles studentsystem
        (FS). Det betyr at data som registreres i Arbeidslivsportalen lagres i
        FS. Data i FS rapporteres i ulike sammenhenger til en rekke
        parter/virksomheter. Se gjerne personvernerklæringen for FS.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. Formålet med, samt rettslig grunnlag for behandling av personopplysninger i Arbeidslivsportalen',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Formål')}

      <p>
        Formålet med behandlingen av personopplysninger i Arbeidslivsportalen er
        at du skal kunne registrere søknad om praksisted og se informasjon om
        deg og ditt praksissted.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Rettslig grunnlag')}

      <p>
        Det rettslige grunnlaget for å behandle personopplysninger i
        Arbeidslivsportalen er GDPR artikkel 6 nr, 1 bokstav e, jf. nr. 3
        bokstav b.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. Hvilke personopplysninger blir behandlet i Arbeidslivsportalen og hvor lenge lagrer vi dine personopplysninger?',
      )}

      <p>
        Per i dag blir det ikke lagret eller behandlet sensitive
        personopplysninger om deg i Arbeidslivsportalen.
      </p>

      <p>
        Når du logger deg på i Arbeidslivsportalen lagrer vi ditt fødselsnummer,
        din IP-adresse og eventuelle feilmeldinger. Denne informasjonen lagres
        for å kunne gi deg bistand ved behov for hjelp i forbindelse med bruk av
        Arbeidslivsportalen. Personopplysningene dine knyttet til dine
        innlogginger lagres i 12 måneder før de slettes.
      </p>

      <p>
        Vi lagrer også informasjon om hvilke handlinger du utfører i
        Arbeidslivsportalen. Denne informasjonen slettes etter 12 måneder.
      </p>

      <p>
        Følgende personopplysninger kan bli behandlet i Arbeidslivsportalen:
        profilinformasjon (slik som ditt navn, fødselsnummer/D-nummer/S-nummer
        (11 siffer), kontaktinformasjon, informasjon om din bakgrunn, morsmål,
        søknadsinformasjon, samtykker du har gitt, informasjon om
        søknadsalternativer og dokumenter du laster opp i forbindelse med
        søknaden.
      </p>

      <p>
        Personopplysninger som registreres i Arbeidslivsportalen sendes til det
        studieadministrative systemet Felles studentsystem (FS). Se
        personvernerklæringen for FS for detaljer om hvilke tredjeparter som
        mottar personopplysninger fra FS. Arbeidslivsportalen deler
        personopplysninger, inkludert fødselsnummer/D-nummer/S-nummer (11
        siffer), med praksissteder i de tilfeller hvor det er nødvendig for
        oppretting av brukertilgang til personal- og journalsystemer eller
        tilsvarende.
      </p>

      <p>Personopplysningene dine kan hentes fra:</p>

      <ul>
        <li>Felles studentsystem (FS)</li>
        <li>Frivillige registreringer</li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '5. I noen tilfeller utfører vi automatisk saksbehandling',
      )}

      <p>
        Når du benytter Arbeidslivsportalen ved{' '}
        {userRole ? userRole + ' ' : 'et lærested'} vil en rekke prosesser
        tilknyttet til søknaden utføres som delvis automatisk eller
        helautomatisk saksbehandling. Det gjelder i følgende tilfeller:
      </p>

      <ul>
        <li>
          Dersom ansvarlig lærested benytter automatisk fordeling av student på
          praksissted
        </li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Vi utleverer dine personopplysninger til andre',
      )}

      <p>
        Utlevering eller eksport av data defineres som enhver avgivelse av data
        foruten til eget system/behandling eller til den registrerte selv eller
        noen som mottar data på dennes vegne.
      </p>

      <p>
        Lærestedet kan utlevere eller eksportere data som inneholder
        personopplysninger til andre systemer, det vil si ekstern databehandler
        i de tilfellene der det anses som nødvendig.
      </p>

      <p>
        Dine personopplysninger blir ikke utlevert til land utenfor EU/EØS,
        eller noen internasjonale organisasjoner. Arbeidslivsportalen benytter
        Amazon Web Services, som er en sikker plattform for skytjenester, hvor
        selve databasen er plassert i Dublin, Irland.
      </p>

      <p>
        Dine personopplysninger kan bli utlevert til følgende
        parter/virksomheter:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Kunnskapssektorens tjenesteleverandør',
          )}
          <p>
            Arbeidslivsportalen utvikles og driftes av Sikt. Ansatte ved Sikt
            som har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Arbeidslivsportalen driftes av Sikt og er integrert med Felles
            studentsystem (FS) som driftes av USIT ved UiO. Ansatte ved USIT som
            har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med
            innloggingsløsningen FEIDE. FEIDE utvikles og leveres av Sikt.
            Ansatte ved Sikt som har behov for det i sin jobb vil ha tilgang til
            ditt FEIDE-navn og IP-adresse. Dette for å kunne utføre brukerstøtte
            og eventuell feilretting i tjenesten. Etter seks måneder slettes
            dine personopplysninger hos FEIDE.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Digitaliseringsdirektoratet (Digdir)',
          )}
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med
            innloggingsløsningene MinID, BankID, Buypass og Commfides via
            ID-porten. Digitaliseringsdirektoratet (Digdir) er
            behandlingsansvarlig for personopplysninger som behandles i
            ID-porten, samt opplysninger som benyttes for å administrere MinID.
          </p>
          <p>
            Hvis du logger inn med ID-porten, kan ansatte i brukerstøtte og
            forvaltning hos Digdir ved behov se ditt fødselsnummer og
            kontaktinformasjon samt tidsavgrenset innloggingshistorikk.
          </p>
          <p>
            Behovet oppstår når Digdir må yte hjelp til bruker eller ved
            feilsøking/-retting av tjenesten.
          </p>
          <p>
            Leverandørene av de elektroniske ID-ene (BankID, Buypass og
            Commfides) er behandlingsansvarlige for personopplysninger som er
            nødvendig for å administrere sine innloggingsløsninger.
          </p>
        </li>
        <li>
          {wrapInHeader(headerStartLevel + 2, 'Andre parter')}
          <p>
            Personopplysninger du registrerer i Arbeidslivsportalen sendes til
            det studieadministrative systemet Felles studentsystem (FS). Se
            personvernerklæringen for FS for mer detaljer om hvilke virksomheter
            som mottar personopplysninger fra FS.
          </p>
        </li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '7. Sikkerheten rundt dine personopplysninger',
      )}

      <p>
        Behandlingsansvarlig gjennomfører regelmessig risiko- og
        sårbarhetsanalyse for å sikre dine personopplysninger i
        Arbeidslivsportalen. I tillegg er det iverksatt sikkerhetstiltak, slik
        som tilgangskontroller, for å forhindre at flere ansatte enn nødvendig
        får tilgang på dine personopplysninger. Ansatte har taushetsplikt rundt
        personopplysninger de får i sitt arbeide.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Dine rettigheter')}

      <p>Se nettside om Dine rettigheter</p>

      {wrapInHeader(headerStartLevel + 1, '9. Kontakt')}

      <p>Se nettside om Kontakt</p>

      {wrapInHeader(headerStartLevel + 2, 'Tjenesteleverandør')}

      <p>
        Sikt – Kunnskapssektorens tjenesteleverandør leverer
        Arbeidslivsportalen. Det vil si at Sikt utvikler og forvalter
        Arbeidslivsportalen og at Sikt har det daglige ansvaret for driften av
        Arbeidslivsportalen. Gjennom dette arbeidet har utvalgte personer ved
        Sikt tilgang til alle personopplysninger som er registrert i
        Arbeidslivsportalen, og med det også personopplysninger som sendes til
        og fra FS.
      </p>
    </>
  ) : (
    <>
      {wrapInHeader(headerStartLevel, 'Privacy Policy')}

      <p>
        This privacy policy describes how we process your personal data in the
        service Arbeidslivsportalen, and the student information system Common
        Student System FS. The purpose of this privacy policy is to provide you
        with information on the processing of personal data in the service and
        what personal data is processed, how, who is responsible, as well as
        your privacy rights, and who you can contact.
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, '')}
        <ol>
          <li>What is personal data?</li>
          <li>Brief description of Arbeidslivsportalen</li>
          <li>
            The purpose of data processing personal data in Arbeidslivsportalen
            and legal basis
          </li>
          <li>
            What types of personal data are processed, and how long is it
            stored?
          </li>
          <li>Automatic processing</li>
          <li>Disclosure of your personal data to third parties</li>
          <li>Personal data safety</li>
          <li>Your rights</li>
          <li>Contact information</li>
        </ol>
      </div>
      {wrapInHeader(headerStartLevel + 1, '1. What is personal data?')}

      <p>
        Personal data is any type of data, information, and assessments that can
        be linked to you as an individual, cf. GDPR Article 4 no. 1. Whether
        data is considered personal information is determined on if it can be
        used to uniquely identify an individual.
      </p>

      <p>
        Data which, on its own, cannot be linked to an individual person may
        constitute uniquely identifiable in cases where the data is used in
        combination with other data.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '2. Brief description of Arbeidslivsportalen',
      )}

      <p>
        Arbeidslivsportalen is a web-application for managing students’ work
        placement. The service is used to plan work placement and students may,
        if made available, apply to work placement locations.
        Arbeidslivsportalen is connected to the student information system
        Common Student System (FS), meaning data registered in
        Arbeidslivsportalen is stored in FS. Data from FS is reported to
        parties/agencies, as described in the Privacy policy for FS.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. The purpose of data processing personal data in Arbeidslivsportalen and legal basis',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Purpose')}

      <p>
        The purpose of processing your personal data in Arbeidslivsportalen is
        to enable you to apply for work placement locations and view your
        information and your work placement location.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Legal basis')}

      <p>
        Processing of personal data in Arbeidslivsportalen is subject to the
        provisions of the General Data Protection Regulation (GDPR) Article 6
        no, 1 litra b. GDPR is a regulation in EU law on data protection and
        privacy in the European Union (EU) and the European Economic Area (EEA)
        which was enforced in 2018.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. What types of personal data are processed, and how long is it stored?',
      )}

      <p>Arbeidslivsportalen does not store any sensitive personal data.</p>

      <table>
        <caption>Data processed and/or stored in Arbeidslivsportalen</caption>
        <tr>
          <th>Type of data</th>
          <th>Purpose of registration and processing</th>
          <th>Erasure/deletion</th>
        </tr>
        <tr>
          <td>
            When logging in:
            <ul>
              <li>
                Your national identity number/D-number/S-number (11 digits)
              </li>
              <li>Your IP address</li>
              <li>Error messages (if applicable)</li>
              <li>FEIDE username (if applicable)</li>
            </ul>
          </td>
          <td>Provide user support and troubleshooting</td>
          <td>Twelve (12) months</td>
        </tr>
        <tr>
          <td>Your actions in the service</td>
          <td />
          <td>Twelve (12) months</td>
        </tr>
      </table>

      <table>
        <caption>
          Data that may be processed and/or stored in Arbeidslivsportalen
        </caption>
        <tr>
          <th>Type of data</th>
          <th>Purpose of registration and processing</th>
          <th>Erasure/deletion</th>
        </tr>
        <tr>
          <td>
            Profile information:
            <ul>
              <li>Name</li>
              <li>Contact information</li>
              <li>Information about your background</li>
              <li>Native language</li>
              <li>
                Application information, application alternatives and uploaded
                documents related to application
              </li>
              <li>Consents you have given</li>
            </ul>
          </td>
          <td>Provide user support and troubleshooting</td>
          <td>Twelve (12) months</td>
        </tr>
      </table>

      <p>
        Personal data registered in Arbeidslivsportalen is sent to the student
        administration system Common Student System (FS). Please read the
        privacy policy for FS for information about which third parties receive
        personal data from FS. Arbeidslivsportalen shares personal data,
        including national ID numbers/D-numbers/S-numbers (11 digits), with
        practice places when it is necessary for granting access to HR and
        journal systems or similar.
      </p>

      <p>Your personal data may be retrieved from:</p>

      <ul>
        <li>Common Student System (FS - Felles studentsystem)</li>
        <li>Voluntary registration</li>
      </ul>

      {wrapInHeader(headerStartLevel + 1, '5. Automatic processing')}

      <p>
        Several processes pertaining to your application may be entirely or
        partly automated when using Arbeidslivsportalen at{' '}
        {userRole ? userRole + ' ' : 'an educational institution'}. Automatic
        processing is used in the following circumstances:
      </p>

      <ul>
        <li>Allocating students to work placement locations (if applicable)</li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Disclosure of your personal data to third parties',
      )}

      <p>
        Disclosure or export of data is defined as any transfer of data aside
        from to the service’s own system/processing, to the data subjects
        themselves, or to someone who receives data on their behalf.
      </p>

      <p>
        The educational institution may disclose or export data including
        personal data to other systems, i.e. external data processors, whenever
        it is deemed necessary.
      </p>

      <p>
        Your personal data will not be disclosed to countries outside of the
        EU/EEA, or to any international organizations. Arbeidslivsportalen uses
        Amazon Web Services which is a secure platform for cloud services, where
        resources (database) are located in Dublin, Irland.
      </p>

      <p>
        Your personal data may be disclosed to the following parties/agencies:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Norwegian Agency for Shared Services in Education and Research',
          )}
          <p>
            Arbeidslivsportalen is developed and operated by Sikt. Sikt staff
            who need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            Arbeidslivsportalen is integrated with the student information
            system Common Student System (FS - Felles Studentsystem) which is
            operated by USIT at the University of Oslo (UiO). USIT staff who
            need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            service FEIDE. FEIDE is developed and provided by Sikt. If you log
            in using FEIDE, Sikt staff may access your FEIDE username and IP
            address, provided they need such access in order to perform their
            duties. They need this access in order to provide user support and,
            if relevant, correct errors as part of their duties. Your personal
            data will be erased from FEIDE after six months.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Agency for Public Management and eGovernment (Digdir)',
          )}
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            services MinID, BankID, Buypass and Commfides through ID-porten.
            ID-porten is a service provided by the Agency for Public Management
            and eGovernment (Digdir). Logging in to Arbeidslivsportalen using
            one of the log-in solutions offered in ID-porten, Digdir staff may
            access your national identity number and any data associated with
            your login to Arbeidslivsportalen, provided they need such access in
            order to perform their duties of providing user support and, if
            relevant, correcting errors.
          </p>
          <p>
            Digdir is the data controller for any data used in the
            administration of MinID. Providers of electronic IDs (BankID,
            Buypass and Commfides) are data controllers for any personal data
            required for the administration of their login services. Please see
            their respective privacy policies for further information.
          </p>
        </li>
        <li>
          {wrapInHeader(headerStartLevel + 2, 'Other parties')}
          <p>
            Any personal data you register in Arbeidslivsportalen is shared with
            the student information system Common Student System (FS - Felles
            Studentsystem). Please see the privacy policy for FS for more
            detailed information about who the recipients of personal data from
            FS are.
          </p>
        </li>
      </ul>

      {wrapInHeader(headerStartLevel + 1, '7. Personal data safety')}

      <p>
        The educational institution regularly conducts regular risk and
        vulnerability analyses and tests the security of the service to ensure
        your personal data are safe in Arbeidslivsportalen. Additionally,
        various security measures have been implemented, such as access control,
        to ensure that your personal data availability is limited to those
        needing access. Employees are required to observe confidentiality of any
        personal data they have access to.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Your rights')}

      <p>
        You have the right to information and access, right to correction, right
        to limit processing, right to erasure, right to object, and right to
        file a complaint against processing. Please see the page Your rights for
        more detailed information.
      </p>

      {wrapInHeader(headerStartLevel + 1, '9. Contact information')}

      <p>Please see the page Contact information.</p>

      {wrapInHeader(headerStartLevel + 2, 'Service provider')}

      <p>
        Sikt – Norwegian Agency for Shared Services in Education and Research is
        the provider of Arbeidslivsportalen. Meaning Sikt develops and maintains
        Arbeidslivsportalen and is responsible for the day-to-day operation of
        Arbeidslivsportalen. As part of this, a select few of Sikt’s staff have
        access to all personal data registered in Arbeidslivsportalen and
        personal data transferred to and from FS.
      </p>
    </>
  );
};

const PrivacyPolicyStudiested: React.FC<
  React.PropsWithChildren<{
    headerStartLevel: number;
    userRole?: string;
  }>
> = ({ headerStartLevel, userRole }) => {
  const { locale } = useIntl();

  return locale === 'nb' ? (
    <>
      {wrapInHeader(
        headerStartLevel,
        'Personvernerklæring for Arbeidslivsportalen',
      )}

      <p>
        Denne personvernerklæringen beskriver hvordan{' '}
        {userRole ? userRole + ' ' : 'det enkelte lærested '}
        håndterer dine personopplysninger i Arbeidslivsportalen. Formålet med
        denne personvernerklæringen er å informere deg om hvilke
        personopplysninger som behandles, hvordan de behandles, hvem som er
        ansvarlig for behandlingen, hvilke rettigheter du har og hvem du kan
        kontakte
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, 'Innhold')}
        <ol>
          <li>Hva er personopplysninger</li>
          <li>Kort om Arbeidslivsportalen</li>
          <li>
            Formålet med behandling av personopplysningeri Arbeidslivsportalen
          </li>
          <li>
            Hvilke personopplysninger blir behandlet i Arbeidslivsportalen en og
            hvor lenge lagrer vi dine personopplysninger?
          </li>
          <li>Automatisk saksbehandling</li>
          <li>Utlevering av dine personopplysninger til andre</li>
          <li>Sikkerheten rundt dine personopplysninger</li>
          <li>Dine rettigheter</li>
          <li>Kontakt</li>
        </ol>
      </div>

      {wrapInHeader(headerStartLevel + 1, '1. Hva er personopplysninger?')}

      <p>
        Personopplysninger er alle former for data, informasjon, opplysninger og
        vurderinger som kan knyttes til deg som enkeltperson, jf. GDPR artikkel
        4 nr. 1. Det avgjørende for om en opplysning er en personopplysning er
        om opplysningene kan knyttes til en identifisert eller identifiserbar
        fysisk person.
      </p>

      <p>
        Opplysninger som alene ikke kan knyttes til en enkeltperson, kan i
        tilfeller hvor de forekommer sammen med andre data utgjøre en
        personopplysning hvis de indirekte identifiserer en person.
      </p>

      {wrapInHeader(headerStartLevel + 1, '2. Kort om Arbeidslivsportalen')}

      <p>
        Arbeidslivsportalen er en webapplikasjon som lar fagpersoner utføre en
        del nødvendige studieadministrative rutiner og oppgaver.
        Arbeidslivsportalen er tenkt å fungere som et støtteverktøy i for
        studenthåndtering av praksis.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. Formålet med behandling av personopplysninger i Arbeidslivsportalen',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Formål')}

      <p>
        Hovedformålet med behandling av dine personopplysninger i
        Arbeidslivsportalen er å gi deg oversikt over din praksisperiode(r),
        samt dine veiledningsforhold for å bistå i administrative oppgaver
        knyttet til praksisundervisning og veiledning.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Rettslig grunnlag')}

      <p>
        Hjemmelsgrunnlaget for behandlingen av personopplysningene i
        Arbeidslivsportalen er Personvernforordningen (GDPR) art. 6 nr. 1
        bokstav b. Personvernforordningen er EUs lovgivning på
        personvernområdet, og gjelder for hele EU/EØS fra 2018. Det rettslige
        grunnlaget for å behandle personopplysninger i Arbeidslivsportalen er
        regulert i avtale mellom deg som ansatt og din arbeidsgiver.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. Hvilke personopplysninger blir behandlet i Arbeidslivsportalen og hvor lenge lagrer vi dine personopplysninger?',
      )}

      <p>
        Det finnes ingen sensitive personopplysninger i Arbeidslivsportalen.
      </p>

      <p>
        Når du logger deg på i Arbeidslivsportalen lagrer vi ditt løpenummer i
        institusjonens FS-database, din IP-adresse og eventuelle feilmeldinger.
        Hvis du logger deg inn med FEIDE, blir også FEIDEbrukernavnet ditt
        logget. Dette for at Sikt skal kunne bistå deg med feilsøking hvis det
        skulle bli behov for det. Personopplysningene dine knyttet til dine
        innlogginger lagres i tre måneder før de slettes.
      </p>

      <p>
        Vi lagrer også informasjon om hvilke handlinger du utfører i
        Arbeidslivsportalen. Denne informasjonen slettes etter 6 måneder.
      </p>

      <p>
        I tillegg blir din identitet knyttet til studieadministrative oppgaver
        du utfører i Arbeidslivsportalen, som innmelding av kapasitet,
        registrering av praksisperiode, plassering av student lagret. Disse
        personopplysningene slettes normalt ikke.
      </p>

      <p>
        Arbeidslivsportalen behandler også informasjon om dine aktive og
        tidligere roller i forbindelse med registrering og eventuell
        kontaktinformasjon som er registrert. Disse personopplysningene lagres
        frem til de eventuelt slettes av institusjonen du jobber ved/for.
      </p>

      <p>
        Opplysninger på studenter, som du registrerer i Arbeidslivsportalen,
        sendes til det studieadministrative systemet Felles studentsystem (FS).
      </p>

      {wrapInHeader(headerStartLevel + 1, '5. Automatisk saksbehandling')}

      <p>
        Personopplysningene dine vil ikke være gjenstand for automatisert
        saksbehandling eller profilering.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Utlevering av dine personopplysninger til andre',
      )}

      <p>
        Utlevering eller eksport av data defineres som enhver avgivelse av data
        foruten til eget system/behandling eller til den registrerte selv eller
        noen som mottar data på dennes vegne.{' '}
        {userRole ? userRole + ' ' : 'Lærestedet'} kan utlevere eller eksportere
        data som inneholder personopplysninger til andre systemer, det vil si
        ekstern databehandler i de tilfellene der det anses som nødvendig
      </p>

      <p>
        Dine personopplysninger blir ikke utlevert til land utenfor EU/EØS,
        eller noen internasjonale organisasjoner. Arbeidslivsportalen benytter
        Amazon Web Services, som er en sikker plattform for skytjenester, hvor
        selve databasen er plassert i Dublin, Irland.
      </p>

      <p>
        Dine personopplysninger kan bli utlevert til følgende
        parter/virksomheter:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Kunnskapssektorens tjenesteleverandør',
          )}
          <p>
            Arbeidslivsportalen utvikles og driftes av Sikt. Ansatte ved Sikt
            som har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Arbeidslivsportalen driftes av Sikt og er integrert med Felles
            studentsystem (FS) som driftes av USIT ved UiO. Ansatte ved USIT som
            har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med FEIDE. FEIDE
            utvikles og leveres av Sikt. Hvis du logger inn med FEIDE vil
            ansatte ved Sikt, som har behov for det i sin jobb, ha tilgang til
            ditt FEIDE-navn og IP-adresse. Dette for å kunne utføre brukerstøtte
            og eventuell feilretting i tjenesten. Etter seks måneder slettes
            dine personopplysninger hos FEIDE.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Digitaliseringsdirektoratet (Digdir)',
          )}
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med
            innloggingsløsningene MinID, BankID, Buypass og Commfides via
            ID-porten. ID-porten tilbys av Digdir. Hvis du logger inn med en av
            innloggingsløsningene i ID-porten, vil ansatte ved Digdir, som har
            behov for det i sin jobb, ha tilgang til ditt fødselsnummer, samt
            data knyttet til din innlogging i Arbeidslivsportalen. Dette for å
            kunne utføre brukerstøtte og eventuell feilretting i tjenesten.
          </p>
          <p>
            Digdir er behandlingsansvarlig for opplysninger som benyttes for å
            administrere MinID. Leverandørene av de elektroniske ID-ene (BankID,
            Buypass og Commfides) er behandlingsansvarlige for
            personopplysninger som er nødvendig for å administrere sine
            innloggingsløsninger. Vi henviser til deres informasjonssider om
            personvern for ytterligere informasjon.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Andre fagpersoner ved din institusjon',
          )}
          <p>
            Innloggede brukere i Arbeidslivsportalen kan se informasjon om dine
            nåværende og tidligere roller, samt din kontaktinformasjon. Dette
            for å tilrettelegge for samhandling i forbindelse med planlegging av
            praksis. Tilsvarende vises ditt navn og kontaktinformasjon til
            praksissteder for å lette samhandling.
          </p>
        </li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '7. Sikkerheten rundt dine personopplysninger',
      )}

      <p>
        {userRole ? userRole + ' ' : 'Lærestedet'} gjennomfører regelmessig
        risiko- og sårbarhetsanalyse av datasystemene vi benytter for å sikre
        dine personopplysninger. I tillegg er det iverksatt sikkerhetstiltak,
        slik som tilgangskontroller, for å forhindre at flere ansatte enn
        nødvendig får tilgang til dine personopplysninger. Ansatte har
        taushetsplikt rundt personopplysninger de får i sitt arbeide.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Dine rettigheter')}

      <p>
        Rettigheter omhandler her rett til informasjon og innsyn, rett til
        korrigering, rett til begrenset behandling, rett til sletting, rett til
        å fremme innsigelse og rett til å klage over behandlingen.
      </p>

      <p>Se nettside om Dine rettigheter.</p>

      {wrapInHeader(headerStartLevel + 1, '9. Kontakt')}

      <p>Se nettside om Kontakt</p>

      {wrapInHeader(headerStartLevel + 2, 'Tjenesteleverandør')}

      <p>
        Sikt – Kunnskapssektorens tjenesteleverandør leverer
        Arbeidslivsportalen. Det vil si at Sikt utvikler og forvalter
        Arbeidslivsportalen og at Sikt har det daglige ansvaret for driften.
        Gjennom dette arbeidet har utvalgte personer ved Sikt tilgang til alle
        personopplysninger som er registrert i Arbeidslivsportalen.
      </p>
    </>
  ) : (
    <>
      {wrapInHeader(headerStartLevel, 'Privacy Policy')}

      <p>
        This privacy policy describes the procedures on the processing,
        collection, use, and disclosure of personal data in the service
        Arbeidslivsportalen, and what personal data is processed, how, who is
        responsible, as well as your privacy rights, and who you can contact.
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, '')}
        <ol>
          <li>What is personal data?</li>
          <li>Brief description of Arbeidslivsportalen</li>
          <li>
            The purpose of data processing personal data in Arbeidslivsportalen
            and legal basis
          </li>
          <li>
            What types of personal data are processed, and how long is it
            stored?
          </li>
          <li>Automatic processing</li>
          <li>Disclosure of your personal data to third parties</li>
          <li>Personal data safety</li>
          <li>Your rights</li>
          <li>Contact information</li>
        </ol>
      </div>
      {wrapInHeader(headerStartLevel + 1, '1. What is personal data?')}

      <p>
        Personal data is any type of data, information, and assessments that can
        be linked to you as an individual, cf. GDPR Article 4 no. 1. Whether
        data is considered personal information is determined on if it can be
        used to uniquely identify an individual.
      </p>

      <p>
        Data which, on its own, cannot be linked to an individual person may
        constitute uniquely identifiable in cases where the data is used in
        combination with other data.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '2. Brief description of Arbeidslivsportalen',
      )}

      <p>
        Arbeidslivsportalen is a web-application for academic staff performing
        studies administrative routines and tasks. The purpose of the service is
        to support managing work placement of students.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. The purpose of data processing personal data in Arbeidslivsportalen and legal basis',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Purpose')}

      <p>
        The purpose of processing your personal data in Arbeidslivsportalen is
        to provide you with an overview of your work placement and supervisory
        relations to aid you in management of work placement and academic
        supervision.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Legal basis')}

      <p>
        Processing of personal data in Arbeidslivsportalen is subject to the
        provisions of the General Data Protection Regulation (GDPR) Article 6
        no, 1 litra b. GDPR is a regulation in EU law on data protection and
        privacy in the European Union (EU) and the European Economic Area (EEA)
        which was enforced in 2018.
      </p>

      <p>
        The legal basis of processing personal data in Arbeidslivsportalen is
        regulated in the contract between you, as an employee, and your
        employer.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. What types of personal data are processed, and how long is it stored?',
      )}

      <p>Arbeidslivsportalen does not store any sensitive personal data.</p>

      <table>
        <caption>Data processed and/or stored in Arbeidslivsportalen</caption>
        <tr>
          <th>Type of data</th>
          <th>Purpose of registration and processing</th>
          <th>Erasure/deletion</th>
        </tr>
        <tr>
          <td>
            When logging in:
            <ul>
              <li>
                Your serial number in the educational institution’s FS database
              </li>
              <li>Your IP address</li>
              <li>Error messages (if applicable)</li>
              <li>FEIDE username (if applicable)</li>
            </ul>
          </td>
          <td>Provide user support and troubleshooting</td>
          <td>Three (3) months</td>
        </tr>
        <tr>
          <td>Your actions in the service</td>
          <td />
          <td>Six (6) months</td>
        </tr>
        <tr>
          <td>
            Linking your user identity to the studies administrative tasks you
            perform, such as:
            <ul>
              <li>Capacity reporting</li>
              <li>Registering work placement</li>
              <li>Allocation of students’ training locations</li>
            </ul>
          </td>
          <td />
          <td>This type of personal data is normally not erased.</td>
        </tr>
        <tr>
          <td>
            Your previous and current roles related to registration, as well as
            any contact information registered in the educational institution’s
            FS database.
          </td>
          <td />
          <td>Retained until the information is erased by your employer.</td>
        </tr>
        <tr>
          <td>
            Data about students registered by you is shared with the student
            information system Common Student System (FS)
          </td>
          <td />
          <td />
        </tr>
      </table>

      <p>
        Any personal data registered in Arbeidslivsportalen is shared with
        student information system Common Student System (FS). In principle, all
        personal data in FS is stored permanently. Please cf. Privacy policy for
        FS.
      </p>

      {wrapInHeader(headerStartLevel + 1, '5. Automatic processing')}

      <p>
        Your personal data will not be made subject to automated processing or
        profiling.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Disclosure of your personal data to third parties',
      )}

      <p>
        Disclosure or export of data is defined as any transfer of data aside
        from to the service’s own system/processing, to the data subjects
        themselves, or to someone who receives data on their behalf.
      </p>

      <p>
        The educational institution may disclose or export data including
        personal data to other systems, i.e. external data processors, whenever
        it is deemed necessary.
      </p>

      <p>
        Your personal data will not be disclosed to countries outside of the
        EU/EEA, or to any international organizations. Arbeidslivsportalen uses
        Amazon Web Services which is a secure platform for cloud services, where
        resources (database) are located in Dublin, Irland.
      </p>

      <p>
        Your personal data may be disclosed to the following parties/agencies:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Norwegian Agency for Shared Services in Education and Research',
          )}
          <p>
            Arbeidslivsportalen is developed and operated by Sikt. Sikt staff
            who need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            Arbeidslivsportalen is integrated with the student information
            system Common Student System (FS - Felles Studentsystem) which is
            operated by USIT at the University of Oslo (UiO). USIT staff who
            need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            service FEIDE. FEIDE is developed and provided by Sikt. If you log
            in using FEIDE, Sikt staff may access your FEIDE username and IP
            address, provided they need such access in order to perform their
            duties. They need this access in order to provide user support and,
            if relevant, correct errors as part of their duties. Your personal
            data will be erased from FEIDE after six months.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Agency for Public Management and eGovernment (Digdir)',
          )}
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            services MinID, BankID, Buypass and Commfides through ID-porten.
            ID-porten is a service provided by the Agency for Public Management
            and eGovernment (Digdir). Logging in to Arbeidslivsportalen using
            one of the log-in solutions offered in ID-porten, Digdir staff may
            access your national identity number and any data associated with
            your login to Arbeidslivsportalen, provided they need such access in
            order to perform their duties of providing user support and, if
            relevant, correcting errors.
          </p>
          <p>
            Digdir is the data controller for any data used in the
            administration of MinID. Providers of electronic IDs (BankID,
            Buypass and Commfides) are data controllers for any personal data
            required for the administration of their login services. Please see
            their respective privacy policies for further information.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Other academic staff at your educational institution',
          )}
          <p>
            Other users who have logged in to Arbeidslivsportalen may see
            information about your current and previous roles, as well as your
            contact information. This access is given to facilitate for
            interaction in connection with planning of work placement. Your name
            and contact information are made available to practical training
            locations in order to facilitate interaction during the training
            process.
          </p>
        </li>
      </ul>

      {wrapInHeader(headerStartLevel + 1, '7. Personal data safety')}

      <p>
        {userRole ? userRole + ' ' : 'The educational institution'} regularly
        conducts regular risk and vulnerability analyses and tests the security
        of the service to ensure your personal data are safe in
        Arbeidslivsportalen. Additionally, various security measures have been
        implemented, such as access control, to ensure that your personal data
        ac is limited to those needing access. Employees are required to observe
        confidentiality of any personal data they have access to.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Your rights')}

      <p>
        You have the right to information and access, right to correction, right
        to limit processing, right to erasure, right to object, and right to
        file a complaint against processing. Please see the page Your rights for
        more detailed information.
      </p>

      {wrapInHeader(headerStartLevel + 1, '9. Contact information')}

      <p>Please see the page Contact information.</p>

      {wrapInHeader(headerStartLevel + 2, 'Service provider')}

      <p>
        Sikt – Norwegian Agency for Shared Services in Education and Research is
        the provider of Arbeidslivsportalen. Meaning Sikt develops and maintains
        Arbeidslivsportalen and is responsible for the day-to-day operation of
        Arbeidslivsportalen. As part of this, a select few of Sikt’s staff have
        access to all personal data registered in Arbeidslivsportalen and
        personal data transferred to and from FS.
      </p>
    </>
  );
};

const PrivacyPolicyPraksissted: React.FC<
  React.PropsWithChildren<{
    headerStartLevel: number;
    userRole?: string;
  }>
> = ({ headerStartLevel, userRole }) => {
  const { locale } = useIntl();

  return locale === 'nb' ? (
    <>
      {wrapInHeader(
        headerStartLevel,
        'Personvernerklæring for Arbeidslivsportalen',
      )}

      <p>
        Denne personvernerklæringen beskriver hvordan din institusjon håndterer
        dine personopplysninger i Arbeidslivsportalen. Formålet med denne
        personvernerklæringen er å informere deg om hvilke personopplysninger
        som behandles, hvordan de behandles, hvem som er ansvarlig for
        behandlingen, hvilke rettigheter du har og hvem du kan kontakte.
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, 'Innhold')}
        <ol>
          <li>Hva er personopplysninger</li>
          <li>Kort om Arbeidslivsportalen</li>
          <li>
            Formålet med behandling av personopplysninger i Arbeidslivsportalen
          </li>
          <li>
            Hvilke personopplysninger blir behandlet i Arbeidslivsportalen en og
            hvor lenge lagrer vi dine personopplysninger?
          </li>
          <li>Automatisk saksbehandling</li>
          <li>Utlevering av dine personopplysninger til andre</li>
          <li>Sikkerheten rundt dine personopplysninger</li>
          <li>Dine rettigheter</li>
          <li>Kontakt</li>
        </ol>
      </div>

      {wrapInHeader(headerStartLevel + 1, '1. Hva er personopplysninger?')}

      <p>
        Personopplysninger er alle former for data, informasjon, opplysninger og
        vurderinger som kan knyttes til deg som enkeltperson, jf. GDPR artikkel
        4 nr. 1. Det avgjørende for om en opplysning er en personopplysning er
        om opplysningene kan knyttes til en identifisert eller identifiserbar
        fysisk person.
      </p>

      <p>
        Opplysninger som alene ikke kan knyttes til en enkeltperson, kan i
        tilfeller hvor de forekommer sammen med andre data utgjøre en
        personopplysning hvis de indirekte identifiserer en person.
      </p>

      {wrapInHeader(headerStartLevel + 1, '2. Kort om Arbeidslivsportalen')}

      <p>
        Arbeidslivsportalen er en webapplikasjon som lar fagpersoner utføre en
        del nødvendige studieadministrative rutiner og oppgaver.
        Arbeidslivsportalen er tenkt å fungere som et støtteverktøy i for
        studenthåndtering av praksis.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. Formålet med behandling av personopplysninger i Arbeidslivsportalen',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Formål')}

      <p>
        Hovedformålet med behandling av dine personopplysninger i
        Arbeidslivsportalen er å gi deg oversikt over din praksisperiode(r),
        samt dine veiledningsforhold for å bistå i administrative oppgaver
        knyttet til praksisundervisning og veiledning.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Rettslig grunnlag')}

      <p>
        Hjemmelsgrunnlaget for behandlingen av personopplysningene i
        Arbeidslivsportalen er Personvernforordningen (GDPR) art. 6 nr. 1
        bokstav b. Personvernforordningen er EUs lovgivning på
        personvernområdet, og gjelder for hele EU/EØS fra 2018. Det rettslige
        grunnlaget for å behandle personopplysninger i Arbeidslivsportalen er
        regulert i avtale mellom deg som ansatt og din arbeidsgiver.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. Hvilke personopplysninger blir behandlet i Arbeidslivsportalen og hvor lenge lagrer vi dine personopplysninger?',
      )}

      <p>
        Det finnes ingen sensitive personopplysninger i Arbeidslivsportalen.
      </p>

      <p>
        Når du logger deg på i Arbeidslivsportalen lagrer vi ditt løpenummer i
        institusjonens FS-database, din IP-adresse og eventuelle feilmeldinger.
        Hvis du logger deg inn med FEIDE, blir også FEIDEbrukernavnet ditt
        logget. Dette for at Sikt skal kunne bistå deg med feilsøking hvis det
        skulle bli behov for det. Personopplysningene dine knyttet til dine
        innlogginger lagres i tre måneder før de slettes.
      </p>

      <p>
        Vi lagrer også informasjon om hvilke handlinger du utfører i
        Arbeidslivsportalen. Denne informasjonen slettes etter 6 måneder.
      </p>

      <p>
        I tillegg blir din identitet knyttet til studieadministrative oppgaver
        du utfører i Arbeidslivsportalen, som innmelding av kapasitet,
        registrering av praksisperiode, plassering av student lagret. Disse
        personopplysningene slettes normalt ikke.
      </p>

      <p>
        Arbeidslivsportalen behandler også informasjon om dine aktive og
        tidligere roller i forbindelse med registrering og eventuell
        kontaktinformasjon som er registrert. Disse personopplysningene lagres
        frem til de eventuelt slettes av institusjonen du jobber ved/for.
      </p>

      <p>
        Opplysninger på studenter, som du registrerer i Arbeidslivsportalen,
        sendes til det studieadministrative systemet Felles studentsystem (FS).
      </p>

      {wrapInHeader(headerStartLevel + 1, '5. Automatisk saksbehandling')}

      <p>
        Personopplysningene dine vil ikke være gjenstand for automatisert
        saksbehandling eller profilering.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Utlevering av dine personopplysninger til andre',
      )}

      <p>
        Utlevering eller eksport av data defineres som enhver avgivelse av data
        foruten til eget system/behandling eller til den registrerte selv eller
        noen som mottar data på dennes vegne.{' '}
        {userRole ? userRole + ' ' : 'Det enkelte lærested'} kan utlevere eller
        eksportere data som inneholder personopplysninger til andre systemer,
        det vil si ekstern databehandler i de tilfellene der det anses som
        nødvendig. Dine personopplysninger blir ikke utlevert til land utenfor
        EU/EØS, eller noen internasjonale organisasjoner. Arbeidslivsportalen
        benytter Amazon Web Services, som er en sikker plattform for
        skytjenester, hvor selve databasen er plassert i Dublin, Irland.
      </p>

      <p>
        Dine personopplysninger kan bli utlevert til følgende
        parter/virksomheter:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Kunnskapssektorens tjenesteleverandør',
          )}
          <p>
            Arbeidslivsportalen utvikles og driftes av Sikt. Ansatte ved Sikt
            som har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Arbeidslivsportalen driftes av Sikt og er integrert med Felles
            studentsystem (FS) som driftes av USIT ved UiO. Ansatte ved USIT som
            har behov for det i sin jobb vil ha tilgang til dine
            personopplysninger. Dette for å kunne utføre brukerstøtte og
            eventuell feilretting i tjenesten.
          </p>
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med FEIDE. FEIDE
            utvikles og leveres av Sikt. Hvis du logger inn med FEIDE vil
            ansatte ved Sikt, som har behov for det i sin jobb, ha tilgang til
            ditt FEIDE-navn og IP-adresse. Dette for å kunne utføre brukerstøtte
            og eventuell feilretting i tjenesten. Etter seks måneder slettes
            dine personopplysninger hos FEIDE.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Digitaliseringsdirektoratet (Digdir)',
          )}
          <p>
            Det er mulig å logge inn i Arbeidslivsportalen med
            innloggingsløsningene MinID, BankID, Buypass og Commfides via
            ID-porten. ID-porten tilbys av Digdir. Hvis du logger inn med en av
            innloggingsløsningene i ID-porten, vil ansatte ved Digdir, som har
            behov for det i sin jobb, ha tilgang til ditt fødselsnummer, samt
            data knyttet til din innlogging i Arbeidslivsportalen. Dette for å
            kunne utføre brukerstøtte og eventuell feilretting i tjenesten.
          </p>
          <p>
            Digdir er behandlingsansvarlig for opplysninger som benyttes for å
            administrere MinID. Leverandørene av de elektroniske ID-ene (BankID,
            Buypass og Commfides) er behandlingsansvarlige for
            personopplysninger som er nødvendig for å administrere sine
            innloggingsløsninger. Vi henviser til deres informasjonssider om
            personvern for ytterligere informasjon.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Andre fagpersoner ved din institusjon',
          )}
          <p>
            Innloggede brukere i Arbeidslivsportalen kan se informasjon om dine
            nåværende og tidligere roller, samt din kontaktinformasjon. Dette
            for å tilrettelegge for samhandling i forbindelse med planlegging av
            praksis. Tilsvarende vises ditt navn og kontaktinformasjon til
            praksissteder for å lette samhandling.
          </p>
        </li>
      </ul>

      {wrapInHeader(
        headerStartLevel + 1,
        '7. Sikkerheten rundt dine personopplysninger',
      )}

      <p>
        Behandlingsansvarlig gjennomfører regelmessig risiko- og
        sårbarhetsanalyse av datasystemene for å sikre dine personopplysninger.
        I tillegg er det iverksatt sikkerhetstiltak, slik som
        tilgangskontroller, for å forhindre at flere ansatte enn nødvendig får
        tilgang til dine personopplysninger. Ansatte har taushetsplikt rundt
        personopplysninger de får i sitt arbeide.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Dine rettigheter')}

      <p>
        Rettigheter omhandler her rett til informasjon og innsyn, rett til
        korrigering, rett til begrenset behandling, rett til sletting, rett til
        å fremme innsigelse og rett til å klage over behandlingen.
      </p>

      <p>Se nettside om Dine rettigheter.</p>

      {wrapInHeader(headerStartLevel + 1, '9. Kontakt')}

      <p>Se nettside om Kontakt</p>

      {wrapInHeader(headerStartLevel + 2, 'Tjenesteleverandør')}

      <p>
        Sikt – Kunnskapssektorens tjenesteleverandør leverer
        Arbeidslivsportalen. Det vil si at Sikt utvikler og forvalter
        Arbeidslivsportalen og at Sikt har det daglige ansvaret for driften.
        Gjennom dette arbeidet har utvalgte personer ved Sikt tilgang til alle
        personopplysninger som er registrert i Arbeidslivsportalen.
      </p>
    </>
  ) : (
    <>
      {wrapInHeader(headerStartLevel, 'Privacy Policy')}

      <p>
        This privacy policy describes the procedures on the processing,
        collection, use, and disclosure of personal data in the service
        Arbeidslivsportalen, and what personal data is processed, how, who is
        responsible, as well as your privacy rights, and who you can contact.
      </p>

      <div className={css.contentSummary}>
        {wrapInHeader(headerStartLevel + 1, '')}
        <ol>
          <li>What is personal data?</li>
          <li>Brief description of Arbeidslivsportalen</li>
          <li>
            The purpose of data processing personal data in Arbeidslivsportalen
            and legal basis
          </li>
          <li>
            What types of personal data are processed, and how long is it
            stored?
          </li>
          <li>Automatic processing</li>
          <li>Disclosure of your personal data to third parties</li>
          <li>Personal data safety</li>
          <li>Your rights</li>
          <li>Contact information</li>
        </ol>
      </div>
      {wrapInHeader(headerStartLevel + 1, '1. What is personal data?')}

      <p>
        Personal data is any type of data, information, and assessments that can
        be linked to you as an individual, cf. GDPR Article 4 no. 1. Whether
        data is considered personal information is determined on if it can be
        used to uniquely identify an individual.
      </p>

      <p>
        Data which, on its own, cannot be linked to an individual person may
        constitute uniquely identifiable in cases where the data is used in
        combination with other data.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '2. Brief description of Arbeidslivsportalen',
      )}

      <p>
        Arbeidslivsportalen is a web-application for academic staff performing
        studies administrative routines and tasks. The purpose of the service is
        to support managing work placement of students.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '3. The purpose of data processing personal data in Arbeidslivsportalen and legal basis',
      )}

      {wrapInHeader(headerStartLevel + 2, 'Purpose')}

      <p>
        The purpose of processing your personal data in Arbeidslivsportalen is
        to provide you with an overview of your work placement and supervisory
        relations to aid you in management of work placement and academic
        supervision.
      </p>

      {wrapInHeader(headerStartLevel + 2, 'Legal basis')}

      <p>
        Processing of personal data in Arbeidslivsportalen is subject to the
        provisions of the General Data Protection Regulation (GDPR) Article 6
        no, 1 litra b. GDPR is a regulation in EU law on data protection and
        privacy in the European Union (EU) and the European Economic Area (EEA)
        which was enforced in 2018.
      </p>

      <p>
        The legal basis of processing personal data in Arbeidslivsportalen is
        regulated in the contract between you, as an employee, and your
        employer.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '4. What types of personal data are processed, and how long is it stored?',
      )}

      <p>Arbeidslivsportalen does not store any sensitive personal data.</p>

      <table>
        <caption>Data processed and/or stored in Arbeidslivsportalen</caption>
        <tr>
          <th>Type of data</th>
          <th>Purpose of registration and processing</th>
          <th>Erasure/deletion</th>
        </tr>
        <tr>
          <td>
            When logging in:
            <ul>
              <li>
                Your national identity number/D-number/S-number (11 digits)
              </li>
              <li>Your IP address</li>
              <li>Error messages (if applicable)</li>
              <li>FEIDE username (if applicable)</li>
            </ul>
          </td>
          <td>Provide user support and troubleshooting</td>
          <td>Three (3) months</td>
        </tr>
        <tr>
          <td>Your actions in the service</td>
          <td />
          <td>Six (6) months</td>
        </tr>
        <tr>
          <td>
            Linking your user identity to the studies administrative tasks you
            perform, such as:
            <ul>
              <li>Capacity reporting</li>
              <li>Registering work placement</li>
              <li>Allocation of students’ training locations</li>
            </ul>
          </td>
          <td />
          <td>This type of personal data is normally not erased.</td>
        </tr>
        <tr>
          <td>
            Your previous and current roles related to registration, as well as
            any contact information registered in the educational institution’s
            FS database.
          </td>
          <td />
          <td>Retained until the information is erased by your employer.</td>
        </tr>
        <tr>
          <td>
            Data about students registered by you is shared with the student
            information system Common Student System (FS)
          </td>
          <td />
          <td />
        </tr>
      </table>

      <p>
        Any personal data registered in Arbeidslivsportalen is shared with
        student information system Common Student System (FS). In principle, all
        personal data in FS is stored permanently. Please cf. Privacy policy for
        FS.
      </p>

      {wrapInHeader(headerStartLevel + 1, '5. Automatic processing')}

      <p>
        Your personal data will not be made subject to automated processing or
        profiling.
      </p>

      {wrapInHeader(
        headerStartLevel + 1,
        '6. Disclosure of your personal data to third parties',
      )}

      <p>
        Disclosure or export of data is defined as any transfer of data aside
        from to the service’s own system/processing, to the data subjects
        themselves, or to someone who receives data on their behalf.
      </p>

      <p>
        The educational institution may disclose or export data including
        personal data to other systems, i.e. external data processors, whenever
        it is deemed necessary.
      </p>

      <p>
        Your personal data will not be disclosed to countries outside of the
        EU/EEA, or to any international organizations. Arbeidslivsportalen uses
        Amazon Web Services which is a secure platform for cloud services, where
        resources (database) are located in Dublin, Irland.
      </p>

      <p>
        Your personal data may be disclosed to the following parties/agencies:
      </p>

      <ul>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Sikt – Norwegian Agency for Shared Services in Education and Research',
          )}
          <p>
            Arbeidslivsportalen is developed and operated by Sikt. Sikt staff
            who need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            Arbeidslivsportalen is integrated with the student information
            system Common Student System (FS - Felles Studentsystem) which is
            operated by USIT at the University of Oslo (UiO). USIT staff who
            need to access your personal data as part of their job will be
            granted such access. They need this access in order to provide user
            support and, if relevant, correct errors as part of their duties.
          </p>
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            service FEIDE. FEIDE is developed and provided by Sikt. If you log
            in using FEIDE, Sikt staff may access your FEIDE username and IP
            address, provided they need such access in order to perform their
            duties. They need this access in order to provide user support and,
            if relevant, correct errors as part of their duties. Your personal
            data will be erased from FEIDE after six months.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Agency for Public Management and eGovernment (Digdir)',
          )}
          <p>
            It is possible to log in to Arbeidslivsportalen using the log-in
            services MinID, BankID, Buypass and Commfides through ID-porten.
            ID-porten is a service provided by the Agency for Public Management
            and eGovernment (Digdir). Logging in to Arbeidslivsportalen using
            one of the log-in solutions offered in ID-porten, Digdir staff may
            access your national identity number and any data associated with
            your login to Arbeidslivsportalen, provided they need such access in
            order to perform their duties of providing user support and, if
            relevant, correcting errors.
          </p>
          <p>
            Digdir is the data controller for any data used in the
            administration of MinID. Providers of electronic IDs (BankID,
            Buypass and Commfides) are data controllers for any personal data
            required for the administration of their login services. Please see
            their respective privacy policies for further information.
          </p>
        </li>
        <li>
          {wrapInHeader(
            headerStartLevel + 2,
            'Other academic staff at your educational institution',
          )}
          <p>
            Other users who have logged in to Arbeidslivsportalen may see
            information about your current and previous roles, as well as your
            contact information. This access is given to facilitate for
            interaction in connection with planning of work placement. Your name
            and contact information are made available to practical training
            locations in order to facilitate interaction during the training
            process.
          </p>
        </li>
      </ul>

      {wrapInHeader(headerStartLevel + 1, '7. Personal data safety')}

      <p>
        {userRole ? userRole + ' ' : 'The educational institution'} regularly
        conducts regular risk and vulnerability analyses and tests the security
        of the service to ensure your personal data are safe in
        Arbeidslivsportalen. Additionally, various security measures have been
        implemented, such as access control, to ensure that your personal data
        ac is limited to those needing access. Employees are required to observe
        confidentiality of any personal data they have access to.
      </p>

      {wrapInHeader(headerStartLevel + 1, '8. Your rights')}

      <p>
        You have the right to information and access, right to correction, right
        to limit processing, right to erasure, right to object, and right to
        file a complaint against processing. Please see the page Your rights for
        more detailed information.
      </p>

      {wrapInHeader(headerStartLevel + 1, '9. Contact information')}

      <p>Please see the page Contact information.</p>

      {wrapInHeader(headerStartLevel + 2, 'Service provider')}

      <p>
        Sikt – Norwegian Agency for Shared Services in Education and Research
        Education and Research is the provider of Arbeidslivsportalen. Meaning
        Sikt develops and maintains Arbeidslivsportalen and is responsible for
        the day-to-day operation of Arbeidslivsportalen. As part of this, a
        select few of Sikt’s staff have access to all personal data registered
        in Arbeidslivsportalen and personal data transferred to and from FS.
      </p>
    </>
  );
};

export const PrivacyPolicy = () => {
  const { loginApi } = useContext(LoginApiContext);
  const { data } = useLoader(() => loginApi.userroleGet(), []);
  const selectedRole = data ? data.selectedRole : undefined;

  const getPrivacyPolicyType = () => {
    if (data?.selectedRole) {
      switch (data.selectedRole.type) {
        case RoletypeDto.Praksissted:
          return (
            <PrivacyPolicyPraksissted
              headerStartLevel={1}
              userRole={selectedRole?.name}
            />
          );
        case RoletypeDto.Student:
          return (
            <PrivacyPolicyStudent
              headerStartLevel={1}
              userRole={selectedRole?.name}
            />
          );
        case RoletypeDto.Studiested:
          return (
            <PrivacyPolicyStudiested
              headerStartLevel={1}
              userRole={selectedRole?.name}
            />
          );
      }
    }
    return <></>;
  };

  return getPrivacyPolicyType();
};

export const PrivacyPolicyLoggedIn: React.FC<
  React.PropsWithChildren<unknown>
> = () => {
  const { formatMessage } = useIntl();

  return (
    <PageLayout title={formatMessage({ id: translations.title.id })} isIndented>
      <div className={css.commonPolicy}>
        <PrivacyPolicy />
      </div>
    </PageLayout>
  );
};

export default RightsAndPrivacyPage;
